import { Checkbox, Input } from 'antd';
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDepartments } from '@/common/useDepartments';
import type { GetDriversWithLastTrackPointQuery } from '@/generated/graphql/graphql';
import {
  countExpressOrders,
  getMostUrgentExpressTimeRemainingMinutes,
} from '../utils/expressOrdersUtils';
import { DriverTile } from './DriverTile';

export const DriverTileListView: FC<{
  data: GetDriversWithLastTrackPointQuery;
  onSelect: Dispatch<SetStateAction<GetDriversWithLastTrackPointQuery['user'][0] | null>>;
  selectedDriverId?: string;
  showExpressOnly: boolean;
  onExpressFilterChange: (checked: boolean) => void;
}> = ({ data, onSelect, selectedDriverId, showExpressOnly, onExpressFilterChange }) => {
  const { selectedDepartment } = useDepartments();
  const departmentTimeZone = selectedDepartment?.timeZone ?? 0;
  const listRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredDrivers = useMemo(() => {
    let drivers = data.user;

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      drivers = drivers.filter((driver) => driver.name.toLowerCase().includes(query));
    }

    if (showExpressOnly) {
      drivers = drivers.filter(
        (driver) => driver.orders && countExpressOrders(driver.orders, departmentTimeZone) > 0
      );
    }

    return [...drivers].sort((a, b) => {
      const timeA = a.orders
        ? getMostUrgentExpressTimeRemainingMinutes(a.orders, departmentTimeZone)
        : null;
      const timeB = b.orders
        ? getMostUrgentExpressTimeRemainingMinutes(b.orders, departmentTimeZone)
        : null;

      const toSortKey = (value: number | null) => {
        if (value === null) {
          return { priority: 1, urgency: Number.POSITIVE_INFINITY };
        }

        return {
          priority: value < 0 ? -1 : 0,
          urgency: value,
        };
      };

      const keyA = toSortKey(timeA);
      const keyB = toSortKey(timeB);

      if (keyA.priority !== keyB.priority) {
        return keyA.priority - keyB.priority;
      }

      return keyA.urgency - keyB.urgency;
    });
  }, [data.user, searchQuery, showExpressOnly, departmentTimeZone]);

  const handleExpressFilterChange = (checked: boolean) => {
    onExpressFilterChange(checked);
    localStorage.setItem('tracking-show-express-only', JSON.stringify(checked));
  };

  useEffect(() => {
    if (selectedDriverId && selectedItemRef.current && listRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedDriverId]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          padding: '8px 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <Input.Search
          placeholder="Search driver by name"
          allowClear
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
        />
        <Checkbox
          checked={showExpressOnly}
          onChange={(e) => handleExpressFilterChange(e.target.checked)}
        >
          Show only drivers with express orders
        </Checkbox>
      </div>
      <div ref={listRef} style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <div className="driver-tile-list">
          <div className="driver-tile-list__items">
            {filteredDrivers.map((driver) => {
              const isSelected = selectedDriverId === driver.id;
              return (
                <div
                  key={driver.id}
                  ref={
                    isSelected
                      ? (el) => {
                          selectedItemRef.current = el;
                        }
                      : undefined
                  }
                >
                  <DriverTile
                    onSelect={() => onSelect(driver)}
                    driver={driver}
                    selected={isSelected}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
